'use client'

import * as React from 'react'
import { FolderKanban, AlertTriangle, FileText, TrendingUp, CheckCircle, Clock, AlertCircle, ThumbsUp, ThumbsDown, Minus, ArrowUpRight, ArrowDownRight, Target, BarChart3 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { mockProjects, mockReports, mockRisks, portfolioStats } from 'lib/mock-data'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts'
import { cn } from 'lib/utils'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProject } from 'lib/access'

const statusColors = {
  active: 'bg-success text-success-foreground',
  'at-risk': 'bg-warning text-warning-foreground',
  'on-hold': 'bg-muted text-muted-foreground',
  completed: 'bg-primary text-primary-foreground',
}

const parseAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]+/g, '')
    const parsed = Number(sanitized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatCurrency = (value, currency = 'KES') => {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatNumber = (value) => {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatCompactKES = (value) => {
  const amount = parseAmount(value)
  if (amount >= 1_000_000_000) return `KES ${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `KES ${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `KES ${(amount / 1_000).toFixed(1)}K`
  return `KES ${Math.round(amount)}`
}

const formatCompactCurrency = (value, currency = 'KES') => {
  const amount = parseAmount(value)
  const compact = new Intl.NumberFormat('en-KE', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount)
  return currency ? `${currency} ${compact}` : compact
}

const normalizeStatusValue = (value) => String(value || '').toLowerCase().replace(/_/g, '-')

const toDateValue = (value) => {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1)
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)

const formatMonthLabel = (date) =>
  date.toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })

const statusProgressMap = {
  completed: 100,
  'in-progress': 60,
  delayed: 40,
  'not-started': 0,
  'on-hold': 20,
}

const getProgressValue = (item) => {
  const raw = parseAmount(item.progress || item.percentComplete || 0)
  if (raw > 0 && raw <= 1) return raw * 100
  if (Number.isFinite(raw) && raw >= 0) return raw
  const status = normalizeStatusValue(item.status)
  return statusProgressMap[status] ?? 0
}


function HealthIcon({ status }) {
  if (status === 'good')
    return (
      <div className="flex items-center justify-center size-6 rounded-full bg-success/15">
        <ThumbsUp className="size-3.5 text-success" />
      </div>
    )
  if (status === 'fair')
    return (
      <div className="flex items-center justify-center size-6 rounded-full bg-warning/15">
        <Minus className="size-3.5 text-warning" />
      </div>
    )
  return (
    <div className="flex items-center justify-center size-6 rounded-full bg-destructive/15">
      <ThumbsDown className="size-3.5 text-destructive" />
    </div>
  )
}

export default function PortfolioDashboard() {
  const { user, accessLevel } = useUser()
  const [portfolioMetrics, setPortfolioMetrics] = React.useState({
    totalProjects: 0,
    activeProjects: 0,
    atRiskProjects: 0,
    planningProjects: 0,
    completedProjects: 0,
    totalBudget: 0,
    totalSpent: 0,
    reportsDue: 0,
    reportsOverdue: 0,
    openRisks: 0,
    resourceAllocated: 0,
    resourceUtilized: 0,
    resourceRate: 0,
  })
  const [metricsLoading, setMetricsLoading] = React.useState(true)
  const [sCurveData, setSCurveData] = React.useState([])
  const [sCurveLoading, setSCurveLoading] = React.useState(true)
  const [budgetByProjectData, setBudgetByProjectData] = React.useState([])
  const [budgetDataLoading, setBudgetDataLoading] = React.useState(true)
  const [healthRows, setHealthRows] = React.useState([])
  const [healthLoading, setHealthLoading] = React.useState(true)
  const [riskDistributionData, setRiskDistributionData] = React.useState([])
  const [riskLoading, setRiskLoading] = React.useState(true)
  const [indicatorPerformance, setIndicatorPerformance] = React.useState([])
  const [indicatorLoading, setIndicatorLoading] = React.useState(true)
  const [reportingCompliance, setReportingCompliance] = React.useState([])
  const [reportingLoading, setReportingLoading] = React.useState(true)
  const [quarterTimeline, setQuarterTimeline] = React.useState({ labels: [], rows: [] })
  const [timelineLoading, setTimelineLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadMetrics() {
      try {
        setHealthLoading(true)
        setRiskLoading(true)
        setIndicatorLoading(true)
        setReportingLoading(true)
        setTimelineLoading(true)
        const [projectSnap, riskSnap, reportSnap, expenditureSnap, deliverableSnap, userSnap, budgetCategorySnap, indicatorSnap] = await Promise.all([
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'risks')),
          getDocs(collection(db, 'reports')),
          getDocs(collection(db, 'expenditures')),
          getDocs(collection(db, 'deliverables')),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'budgetCategories')),
          getDocs(collection(db, 'indicators')),
        ])
        if (!isMounted) return
        const projects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const risks = riskSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const reports = reportSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const expenditures = expenditureSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const deliverables = deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const users = userSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const budgetCategories = budgetCategorySnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const indicators = indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))

        const limited = accessLevel === 'limited'
        const currentUserId = getUserId(user)
        const scopedProjects = limited
          ? projects.filter((project) => isUserAssociatedWithProject(project, currentUserId))
          : projects
        const scopedProjectIds = new Set(scopedProjects.map((project) => project.id))
        const scopedRisks = limited ? risks.filter((risk) => scopedProjectIds.has(risk.projectId)) : risks
        const scopedReports = limited ? reports.filter((report) => scopedProjectIds.has(report.projectId)) : reports
        const scopedExpenditures = limited ? expenditures.filter((exp) => scopedProjectIds.has(exp.projectId)) : expenditures
        const scopedDeliverables = limited ? deliverables.filter((item) => scopedProjectIds.has(item.projectId)) : deliverables
        const scopedBudgetCategories = limited ? budgetCategories.filter((item) => scopedProjectIds.has(item.projectId)) : budgetCategories
        const scopedIndicators = limited ? indicators.filter((item) => scopedProjectIds.has(item.projectId)) : indicators

        const associatedUserIds = new Set()
        scopedProjects.forEach((project) => {
          if (project.principalInvestigatorId) associatedUserIds.add(project.principalInvestigatorId)
          if (project.projectManagerId) associatedUserIds.add(project.projectManagerId)
          if (Array.isArray(project.coInvestigatorIds)) {
            project.coInvestigatorIds.forEach((id) => id && associatedUserIds.add(id))
          }
          if (Array.isArray(project.projectMembers)) {
            project.projectMembers.forEach((member) => {
              if (member?.userId) associatedUserIds.add(member.userId)
              if (member?.id) associatedUserIds.add(member.id)
              if (typeof member === 'string') associatedUserIds.add(member)
            })
          }
        })
        if (currentUserId) associatedUserIds.add(currentUserId)
        const scopedUsers = limited
          ? users.filter((userItem) => associatedUserIds.has(userItem.userId || userItem.id))
          : users

        const activeProjects = scopedProjects.filter((p) => normalizeStatusValue(p.status) === 'active').length
        const planningProjects = scopedProjects.filter((p) => normalizeStatusValue(p.status) === 'planning').length
        const completedProjects = scopedProjects.filter((p) => normalizeStatusValue(p.status) === 'completed').length
        const totalProjects = scopedProjects.length

        const openRisks = scopedRisks.filter((r) => normalizeStatusValue(r.status) === 'open').length
        const riskBuckets = { Low: 0, Medium: 0, High: 0, Critical: 0 }
        scopedRisks.forEach((risk) => {
          if (normalizeStatusValue(risk.status) !== 'open') return
          const score = Number(risk.riskScore || 0)
          if (score >= 15) riskBuckets.Critical += 1
          else if (score >= 10) riskBuckets.High += 1
          else if (score >= 5) riskBuckets.Medium += 1
          else riskBuckets.Low += 1
        })
        const highRiskProjectIds = new Set(
          scopedRisks
            .filter((r) => normalizeStatusValue(r.status) === 'open' && Number(r.riskScore || 0) >= 10)
            .map((r) => r.projectId)
            .filter(Boolean),
        )
        const atRiskProjects = scopedProjects.filter((p) => {
          const status = normalizeStatusValue(p.status)
          if (status === 'at-risk' || status === 'at risk' || status === 'at_risk') return true
          return highRiskProjectIds.has(p.id)
        }).length

        const totalBudget = scopedProjects.reduce((sum, p) => {
          const directBudget = parseAmount(p.totalBudget)
          if (directBudget > 0) return sum + directBudget
          const items = Array.isArray(p.budgetItems) ? p.budgetItems : []
          const itemsTotal = items.reduce(
            (itemSum, item) => itemSum + parseAmount(item.total || item.approved || 0),
            0,
          )
          return sum + itemsTotal
        }, 0)
        const totalSpent = scopedExpenditures.reduce((sum, exp) => sum + parseAmount(exp.amount || 0), 0)

        const reportsDue = scopedReports.filter((r) => {
          const status = normalizeStatusValue(r.status)
          return status === 'draft' || status === 'overdue'
        }).length
        const reportsOverdue = scopedReports.filter((r) => normalizeStatusValue(r.status) === 'overdue').length

        const allocatedResources = scopedUsers.filter((userItem) => userItem.isActive !== false).length
        const assignedUserIds = new Set()
        scopedProjects.forEach((project) => {
          if (project.principalInvestigatorId) assignedUserIds.add(project.principalInvestigatorId)
          if (project.projectManagerId) assignedUserIds.add(project.projectManagerId)
          if (Array.isArray(project.coInvestigatorIds)) {
            project.coInvestigatorIds.forEach((id) => id && assignedUserIds.add(id))
          }
          if (Array.isArray(project.projectMembers)) {
            project.projectMembers.forEach((member) => {
              if (member?.userId) assignedUserIds.add(member.userId)
              if (member?.id) assignedUserIds.add(member.id)
            })
          }
        })
        const utilizedResources = scopedUsers.filter(
          (userItem) => userItem.isActive !== false && assignedUserIds.has(userItem.userId || userItem.id),
        ).length
        const resourceRate = allocatedResources > 0
          ? Math.round((utilizedResources / allocatedResources) * 100)
          : 0

        const getHealth = (value) => {
          if (value > 70) return 'good'
          if (value > 40) return 'fair'
          return 'poor'
        }

        const getBudgetHealth = (budgeted, actual) => {
          if (budgeted <= 0) return 'fair'
          const utilization = (actual / budgeted) * 100
          if (utilization <= 80) return 'good'
          if (utilization <= 100) return 'fair'
          return 'poor'
        }

        const deliverablesByProject = scopedDeliverables.reduce((acc, item) => {
          const projectId = item.projectId
          if (!projectId) return acc
          acc[projectId] = acc[projectId] ? [...acc[projectId], item] : [item]
          return acc
        }, {})

        const risksByProject = scopedRisks.reduce((acc, item) => {
          const projectId = item.projectId
          if (!projectId) return acc
          acc[projectId] = acc[projectId] ? [...acc[projectId], item] : [item]
          return acc
        }, {})

        const budgetCategoriesByProject = scopedBudgetCategories.reduce((acc, item) => {
          const projectId = item.projectId
          if (!projectId) return acc
          const approved = parseAmount(item.approved || item.total || 0)
          acc[projectId] = (acc[projectId] || 0) + approved
          return acc
        }, {})

        const expendituresByProject = scopedExpenditures.reduce((acc, item) => {
          const projectId = item.projectId
          if (!projectId) return acc
          const amount = parseAmount(item.amount || 0)
          acc[projectId] = (acc[projectId] || 0) + amount
          return acc
        }, {})

        const computedBudgetByProject = scopedProjects.map((project) => {
          const projectId = project.id
          const budgetedFromCategories = budgetCategoriesByProject[projectId]
          let budgeted = Number.isFinite(budgetedFromCategories) ? budgetedFromCategories : 0
          if (!budgeted) {
            const directBudget = parseAmount(project.totalBudget || 0)
            if (directBudget > 0) {
              budgeted = directBudget
            } else {
              const items = Array.isArray(project.budgetItems) ? project.budgetItems : []
              budgeted = items.reduce(
                (sum, item) => sum + parseAmount(item.total || item.approved || 0),
                0,
              )
            }
          }
          const actual = expendituresByProject[projectId] || 0
          return {
            id: projectId,
            name: project.code || project.shortCode || project.title || projectId,
            budgeted,
            actual,
            committed: 0,
            currency: project.currency || 'KES',
          }
        })

        setBudgetByProjectData(computedBudgetByProject)

        const now = new Date()

        const healthRowsComputed = scopedProjects.map((project) => {
          const projectId = project.id
          const projectDeliverables = deliverablesByProject[projectId] || []
          const projectRisks = risksByProject[projectId] || []

          let plannedSum = 0
          let plannedCount = 0
          let actualSum = 0
          let actualCount = 0

          projectDeliverables.forEach((item) => {
            const actualProgress = getProgressValue(item)
            if (Number.isFinite(actualProgress)) {
              actualSum += actualProgress
              actualCount += 1
            }
            const start = toDateValue(item.plannedStart)
            const end = toDateValue(item.plannedEnd)
            if (start && end) {
              const totalDuration = Math.max(end.getTime() - start.getTime(), 1)
              let plannedValue = 0
              if (now < start) plannedValue = 0
              else if (now >= end) plannedValue = 100
              else plannedValue = ((now.getTime() - start.getTime()) / totalDuration) * 100
              plannedSum += plannedValue
              plannedCount += 1
            }
          })

          const plannedAvg = plannedCount > 0 ? plannedSum / plannedCount : 0
          const actualAvg = actualCount > 0 ? actualSum / actualCount : 0
          const scheduleScore = plannedAvg > 0 ? (actualAvg / plannedAvg) * 100 : actualAvg
          const scheduleHealth = getHealth(Math.min(100, Math.max(0, scheduleScore)))

          const budgeted = computedBudgetByProject.find((item) => item.id === projectId)?.budgeted || 0
          const actual = expendituresByProject[projectId] || 0
          const budgetHealth = getBudgetHealth(budgeted, actual)

          const openProjectRisks = projectRisks.filter(
            (risk) => normalizeStatusValue(risk.status) === 'open',
          )
          const hasHighRisk = openProjectRisks.some((risk) => Number(risk.riskScore || 0) >= 10)
          const riskHealth = hasHighRisk ? 'poor' : openProjectRisks.length > 0 ? 'fair' : 'good'

          const memberCount =
            (Array.isArray(project.projectMembers) ? project.projectMembers.length : 0)
            + (project.principalInvestigatorId ? 1 : 0)
            + (project.projectManagerId ? 1 : 0)
            + (Array.isArray(project.coInvestigatorIds) ? project.coInvestigatorIds.length : 0)
          const resourcesHealth = memberCount >= 3 ? 'good' : memberCount >= 1 ? 'fair' : 'poor'

          const overall = [scheduleHealth, budgetHealth, riskHealth, resourcesHealth].includes('poor')
            ? 'poor'
            : [scheduleHealth, budgetHealth, riskHealth, resourcesHealth].includes('fair')
              ? 'fair'
              : 'good'

          return {
            project: project.code || project.shortCode || project.id,
            title: project.title || 'Untitled project',
            schedule: scheduleHealth,
            budget: budgetHealth,
            risk: riskHealth,
            resources: resourcesHealth,
            overall,
          }
        })

        setHealthRows(healthRowsComputed)
        setRiskDistributionData([
          { level: 'Low', count: riskBuckets.Low, color: 'oklch(0.55 0.16 150)' },
          { level: 'Medium', count: riskBuckets.Medium, color: 'oklch(0.87 0.15 85)' },
          { level: 'High', count: riskBuckets.High, color: 'oklch(0.7 0.17 65)' },
          { level: 'Critical', count: riskBuckets.Critical, color: 'oklch(0.577 0.245 27.325)' },
        ])

        const indicatorBuckets = { onTrack: 0, atRisk: 0, offTrack: 0 }
        scopedIndicators.forEach((indicator) => {
          const target = parseAmount(indicator.target ?? indicator.targetValue ?? 0)
          const current = parseAmount(indicator.currentValue ?? indicator.current ?? indicator.value ?? 0)
          const ratio = target > 0 ? current / target : 0
          if (ratio >= 0.8) indicatorBuckets.onTrack += 1
          else if (ratio >= 0.5) indicatorBuckets.atRisk += 1
          else indicatorBuckets.offTrack += 1
        })

        const totalIndicators = scopedIndicators.length || 1
        const indicatorRows = [
          {
            name: 'On Track',
            count: indicatorBuckets.onTrack,
            color: 'text-success',
            bg: 'bg-success/10',
            barBg: 'bg-success',
            pct: Math.round((indicatorBuckets.onTrack / totalIndicators) * 100),
            icon: <CheckCircle className="size-4" />,
          },
          {
            name: 'At Risk',
            count: indicatorBuckets.atRisk,
            color: 'text-warning',
            bg: 'bg-warning/10',
            barBg: 'bg-warning',
            pct: Math.round((indicatorBuckets.atRisk / totalIndicators) * 100),
            icon: <AlertTriangle className="size-4" />,
          },
          {
            name: 'Off Track',
            count: indicatorBuckets.offTrack,
            color: 'text-destructive',
            bg: 'bg-destructive/10',
            barBg: 'bg-destructive',
            pct: Math.round((indicatorBuckets.offTrack / totalIndicators) * 100),
            icon: <AlertCircle className="size-4" />,
          },
        ]

        setIndicatorPerformance(indicatorRows)

        const projectById = scopedProjects.reduce((acc, project) => {
          acc[project.id] = project
          return acc
        }, {})
        const complianceRows = scopedReports.map((report) => ({
          id: report.id,
          reportType: report.reportType || report.type || 'report',
          status: normalizeStatusValue(report.status) || 'draft',
          dueDate: report.dueDate || report.submissionDeadline || report.due || null,
          projectCode:
            projectById[report.projectId]?.code
              || projectById[report.projectId]?.shortCode
              || projectById[report.projectId]?.title
              || report.projectId
              || 'Project',
        }))

        setReportingCompliance(complianceRows)

        const projectTimelineRows = scopedProjects.map((project) => {
          const start = toDateValue(project.startDate)
          const end = toDateValue(project.endDate)
          const safeStart = start || now
          const safeEnd = end || now
          const span = Math.max(safeEnd.getTime() - safeStart.getTime(), 1)
          const progress = Math.round(((now.getTime() - safeStart.getTime()) / span) * 100)
          return {
            id: project.id,
            code: project.code || project.shortCode || project.id,
            title: project.title || 'Untitled project',
            start: safeStart,
            end: safeEnd,
            progress: Math.min(100, Math.max(0, Number.isFinite(progress) ? progress : 0)),
          }
        })

        const timelineDates = projectTimelineRows.flatMap((row) => [row.start, row.end]).filter(Boolean)
        let timelineStart = startOfMonth(addMonths(now, -6))
        let timelineEnd = startOfMonth(now)
        if (timelineDates.length > 0) {
          timelineStart = startOfMonth(new Date(Math.min(...timelineDates.map((d) => d.getTime()))))
          timelineEnd = startOfMonth(new Date(Math.max(...timelineDates.map((d) => d.getTime()))))
        }
        const quarterLabels = []
        const quarterStarts = []
        const quarterStartMonth = Math.floor(timelineStart.getMonth() / 3) * 3
        let qCursor = new Date(timelineStart.getFullYear(), quarterStartMonth, 1)
        while (qCursor <= timelineEnd && quarterLabels.length < 4) {
          const quarter = Math.floor(qCursor.getMonth() / 3) + 1
          quarterLabels.push(`Q${quarter} ${String(qCursor.getFullYear()).slice(-2)}`)
          quarterStarts.push(new Date(qCursor))
          qCursor = addMonths(qCursor, 3)
        }
        if (quarterStarts.length > 0) {
          timelineStart = quarterStarts[0]
          timelineEnd = addMonths(quarterStarts[quarterStarts.length - 1], 3)
        }
        const timelineSpan = Math.max(timelineEnd.getTime() - timelineStart.getTime(), 1)

        const mappedRows = projectTimelineRows.map((row) => {
          const leftPct = ((row.start.getTime() - timelineStart.getTime()) / timelineSpan) * 100
          const widthPct = ((row.end.getTime() - row.start.getTime()) / timelineSpan) * 100
          const safeLeft = Math.max(0, Math.min(100, leftPct))
          const safeWidth = Math.max(2, Math.min(100 - safeLeft, widthPct))
          return {
            ...row,
            leftPct: safeLeft,
            widthPct: safeWidth,
            endPct: Math.min(100, safeLeft + safeWidth),
          }
        })

        setQuarterTimeline({
          labels: quarterLabels,
          rows: mappedRows,
        })

        const dateValues = scopedDeliverables.flatMap((item) => {
          const start = toDateValue(item.plannedStart)
          const end = toDateValue(item.plannedEnd)
          return [start, end].filter(Boolean)
        })
        let startDate = startOfMonth(addMonths(now, -5))
        let endDate = startOfMonth(now)

        if (dateValues.length > 0) {
          const minDate = startOfMonth(new Date(Math.min(...dateValues.map((d) => d.getTime()))))
          const maxDate = startOfMonth(new Date(Math.max(...dateValues.map((d) => d.getTime()))))
          endDate = maxDate
          const monthSpan = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + (maxDate.getMonth() - minDate.getMonth())
          if (monthSpan > 11) {
            startDate = addMonths(maxDate, -11)
          } else {
            startDate = minDate
          }
        }

        const months = []
        let cursor = new Date(startDate)
        while (cursor <= endDate && months.length < 12) {
          months.push(new Date(cursor))
          cursor = addMonths(cursor, 1)
        }

        const sCurve = months.map((monthDate) => {
          const monthEnd = endOfMonth(monthDate)
          let plannedSum = 0
          let actualSum = 0
          let count = 0

          scopedDeliverables.forEach((item) => {
            const start = toDateValue(item.plannedStart)
            const end = toDateValue(item.plannedEnd)
            if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return
            const totalDuration = Math.max(end.getTime() - start.getTime(), 1)
            let plannedValue = 0
            if (monthEnd < start) plannedValue = 0
            else if (monthEnd >= end) plannedValue = 100
            else plannedValue = ((monthEnd.getTime() - start.getTime()) / totalDuration) * 100

            const actualProgress = getProgressValue(item)
            const actualValue = monthEnd <= now ? Math.min(plannedValue, actualProgress) : actualProgress

            plannedSum += plannedValue
            actualSum += actualValue
            count += 1
          })

          const plannedAvg = count > 0 ? plannedSum / count : 0
          const actualAvg = count > 0 ? actualSum / count : 0

          return {
            month: formatMonthLabel(monthDate),
            planned: Math.min(100, Math.max(0, Math.round(plannedAvg))),
            actual: Math.min(100, Math.max(0, Math.round(actualAvg))),
          }
        })

        setSCurveData(sCurve)

        setPortfolioMetrics({
          totalProjects,
          activeProjects,
          atRiskProjects,
          planningProjects,
          completedProjects,
          totalBudget,
          totalSpent,
          reportsDue,
          reportsOverdue,
          openRisks,
          resourceAllocated: allocatedResources,
          resourceUtilized: utilizedResources,
          resourceRate,
        })
      } catch {
        if (!isMounted) return
        setPortfolioMetrics({
          totalProjects: 0,
          activeProjects: 0,
          atRiskProjects: 0,
          planningProjects: 0,
          completedProjects: 0,
          totalBudget: 0,
          totalSpent: 0,
          reportsDue: 0,
          reportsOverdue: 0,
          openRisks: 0,
          resourceAllocated: 0,
          resourceUtilized: 0,
          resourceRate: 0,
        })
        setSCurveData([])
        setBudgetByProjectData([])
        setHealthRows([])
        setRiskDistributionData([])
        setIndicatorPerformance([])
        setReportingCompliance([])
        setQuarterTimeline({ labels: [], rows: [] })
      } finally {
        if (isMounted) {
          setMetricsLoading(false)
          setSCurveLoading(false)
          setBudgetDataLoading(false)
          setHealthLoading(false)
          setRiskLoading(false)
          setIndicatorLoading(false)
          setReportingLoading(false)
          setTimelineLoading(false)
        }
      }
    }

    loadMetrics()

    return () => {
      isMounted = false
    }
  }, [accessLevel, user?.userId, user?.uid])

  const budgetUtilization = portfolioMetrics.totalBudget > 0
    ? Math.round((portfolioMetrics.totalSpent / portfolioMetrics.totalBudget) * 100)
    : 0
  const radialData = [
    {
      name: 'Utilization',
      value: budgetUtilization,
      fill: 'oklch(0.35 0.12 145)',
    },
  ]
  const pieData = [
    { name: 'Active', value: portfolioMetrics.activeProjects, color: 'oklch(0.55 0.16 150)' },
    { name: 'Planning', value: portfolioMetrics.planningProjects, color: 'oklch(0.62 0.12 240)' },
    { name: 'At Risk', value: portfolioMetrics.atRiskProjects, color: 'oklch(0.7 0.17 65)' },
    { name: 'Completed', value: portfolioMetrics.completedProjects, color: 'oklch(0.35 0.12 145)' },
  ]

  const latestSCurve = sCurveData[sCurveData.length - 1]
  const planRatio = latestSCurve?.planned > 0
    ? Math.round((latestSCurve.actual / latestSCurve.planned) * 100)
    : 0

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
          Portfolio Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of all grants, projects, and performance metrics
        </p>
      </div>

      {/* Stats Row - Inspired by Financial Dashboard KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Projects</p>
                <p className="text-3xl font-bold text-foreground mt-1">{metricsLoading ? '—' : portfolioMetrics.totalProjects}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{metricsLoading ? '—' : portfolioMetrics.activeProjects} active</p>
              </div>
              <div className="rounded-lg bg-primary/10 p-2">
                <FolderKanban className="size-5 text-primary" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>

        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">At-Risk</p>
                <p className="text-3xl font-bold text-warning mt-1">{metricsLoading ? '—' : portfolioMetrics.atRiskProjects}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Needs attention</p>
              </div>
              <div className="rounded-lg bg-warning/10 p-2">
                <AlertTriangle className="size-5 text-warning" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-warning" />
          </CardContent>
        </Card>

        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Budget Worth (in KES)</p>
                <p className="text-2xl font-semibold text-foreground mt-1">
                  {metricsLoading ? '—' : formatNumber(portfolioMetrics.totalBudget)}
                </p>
                <p className="text-xs text-success flex items-center gap-1 mt-0.5">
                  <ArrowUpRight className="size-3" />
                  {metricsLoading ? '—' : `${budgetUtilization}% utilized`}
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary" />
          </CardContent>
        </Card>

        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Reports Due</p>
                <p className="text-3xl font-bold text-foreground mt-1">{metricsLoading ? '—' : portfolioMetrics.reportsDue}</p>
                <p className="text-xs text-destructive flex items-center gap-1 mt-0.5">
                  <ArrowDownRight className="size-3" />
                  {metricsLoading ? '—' : portfolioMetrics.reportsOverdue} overdue
                </p>
              </div>
              <div className="rounded-lg bg-destructive/10 p-2">
                <FileText className="size-5 text-destructive" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>

        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Open Risks</p>
                <p className="text-3xl font-bold text-foreground mt-1">{metricsLoading ? '—' : portfolioMetrics.openRisks}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Across all projects</p>
              </div>
              <div className="rounded-lg bg-warning/10 p-2">
                <TrendingUp className="size-5 text-warning" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-chart-4" />
          </CardContent>
        </Card>
      </div>

      {/* S-Curve + Budget Bar Chart Row - Inspired by Progress Monitor */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* S-Curve Progress */}
        <Card className="lg:col-span-2 border border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold text-foreground">
                  Portfolio S-Curve: Planned vs Actual Progress
                </CardTitle>
                <CardDescription className="text-xs">
                  Aggregate completion tracking over time
                </CardDescription>
              </div>
              <Badge className="bg-warning/15 text-warning text-[10px]">
                {sCurveLoading ? '—' : `${planRatio}% of plan`}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={sCurveData}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 11 }} stroke="oklch(0.48 0.03 145)" unit="%" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid oklch(0.9 0.01 145)',
                    background: 'oklch(1 0 0)',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="planned"
                  stroke="oklch(0.35 0.12 145)"
                  strokeWidth={2.5}
                  strokeDasharray="6 3"
                  dot={{ r: 4, fill: 'oklch(0.35 0.12 145)' }}
                  name="Planned Progress"
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="oklch(0.87 0.15 85)"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: 'oklch(0.87 0.15 85)' }}
                  name="Actual Progress"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Project Status Donut + Resource Overview */}
        <div className="flex flex-col gap-6">
          <Card className="border border-border">
            <CardHeader className="pb-1">
              <CardTitle className="text-sm font-semibold text-foreground">
                Projects by Status
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-1">
                {pieData.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-1.5">
                    <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Resource Overview - Inspired by Portfolio Management Slide */}
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-foreground">
                Resource Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="rounded-lg bg-primary/10 p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Resources Allocated</p>
                <p className="text-2xl font-bold text-primary">
                  {metricsLoading ? '—' : portfolioMetrics.resourceAllocated}
                </p>
              </div>
              <div className="rounded-lg bg-success/10 p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Resources Utilized</p>
                <p className="text-2xl font-bold text-success">
                  {metricsLoading ? '—' : portfolioMetrics.resourceUtilized}
                </p>
              </div>
              <div className="rounded-lg bg-secondary/30 p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Utilization Rate</p>
                <p className="text-2xl font-bold text-secondary-foreground">
                  {metricsLoading ? '—' : `${portfolioMetrics.resourceRate}%`}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Budget + Portfolio Financing Row - Inspired by Financial Dashboard */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Budget Bar Chart */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">
              Budget vs Actual by Project
            </CardTitle>
            <CardDescription className="text-xs">
              Financial tracking across all active grants (KES)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {budgetDataLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading budget data...</div>
            ) : budgetByProjectData.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No budget data available.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={budgetByProjectData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                  <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" tickFormatter={(v) => formatCompactKES(v)} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid oklch(0.9 0.01 145)' }}
                    formatter={(value) => formatCompactKES(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="budgeted" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} name="Budgeted" />
                  <Bar dataKey="actual" fill="oklch(0.87 0.15 85)" radius={[4, 4, 0, 0]} name="Actual Spent" />
                  <Bar dataKey="committed" fill="oklch(0.55 0.16 150)" radius={[4, 4, 0, 0]} name="Committed" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Portfolio Financing Table - Inspired by PPM Dashboard */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">
              Portfolio Financing
            </CardTitle>
            <CardDescription className="text-xs">
              Planned vs actual expenditure by award
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Project</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-primary uppercase tracking-wider">Planned</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-success uppercase tracking-wider">Actual</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetDataLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                        Loading portfolio financing...
                      </td>
                    </tr>
                  ) : budgetByProjectData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No projects found.
                      </td>
                    </tr>
                  ) : (
                    budgetByProjectData.map((project) => {
                      const variance = project.budgeted - project.actual
                      return (
                        <tr key={project.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-medium text-foreground">{project.name}</span>
                            <span className="text-[10px] text-muted-foreground">Project</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium text-foreground">
                          {formatCompactCurrency(project.budgeted, project.currency)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium text-success">
                          {formatCompactCurrency(project.actual, project.currency)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={cn('text-xs font-medium', variance >= 0 ? 'text-success' : 'text-destructive')}>
                            {variance >= 0 ? '+' : '-'}
                            {formatCompactCurrency(Math.abs(variance), project.currency)}
                          </span>
                        </td>
                      </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Health Card - Inspired by PPM Dashboard */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">
            Project Health Card
          </CardTitle>
          <CardDescription className="text-xs">
            Multi-dimensional health assessment across all projects
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Project</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Schedule</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Budget</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Risk</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Resources</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Overall</th>
                </tr>
              </thead>
              <tbody>
                {healthLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Loading project health...
                    </td>
                  </tr>
                ) : healthRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No projects found.
                    </td>
                  </tr>
                ) : (
                  healthRows.map((row) => (
                    <tr key={row.project} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-mono font-semibold text-foreground">{row.project}</span>
                          <span className="text-[10px] text-muted-foreground truncate max-w-[200px]">{row.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center"><HealthIcon status={row.schedule} /></td>
                      <td className="px-4 py-3 text-center"><HealthIcon status={row.budget} /></td>
                      <td className="px-4 py-3 text-center"><HealthIcon status={row.risk} /></td>
                      <td className="px-4 py-3 text-center"><HealthIcon status={row.resources} /></td>
                      <td className="px-4 py-3 text-center"><HealthIcon status={row.overall} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Risk Report + Indicator Performance + Reports Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Risk Report Bar - Inspired by PPM Dashboard */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">
              Risk Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            {riskLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading risks...</div>
            ) : riskDistributionData.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No risks found.</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={riskDistributionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                  <YAxis type="category" dataKey="level" tick={{ fontSize: 11 }} stroke="oklch(0.48 0.03 145)" width={60} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Risks">
                    {riskDistributionData.map((entry) => (
                      <Cell key={entry.level} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Indicator Performance */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">
              Indicator Performance
            </CardTitle>
            <CardDescription className="text-xs">
              MEL indicator achievement rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              {indicatorLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Loading indicators...</div>
              ) : indicatorPerformance.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No indicators found.</div>
              ) : (
                indicatorPerformance.map((item) => (
                  <div key={item.name} className={cn('rounded-lg p-3', item.bg)}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={item.color}>{item.icon}</span>
                        <span className="text-xs font-medium text-foreground">{item.name}</span>
                      </div>
                      <span className={cn('text-lg font-bold', item.color)}>{item.count}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-foreground/5">
                      <div className={cn('h-full rounded-full', item.barBg)} style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reporting Compliance */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">
              Reporting Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {reportingLoading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading reports...</div>
              ) : reportingCompliance.length === 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">No reports found.</div>
              ) : (
                reportingCompliance.map((report) => {
                  const isOverdue = report.status === 'overdue'
                  return (
                    <div
                      key={report.id}
                      className={cn(
                        'flex items-center justify-between rounded-lg border border-border p-3',
                        isOverdue && 'bg-destructive/5 border-destructive/20'
                      )}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-foreground capitalize">
                          {report.reportType} Report
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {report.projectCode} &middot; Due {report.dueDate || '—'}
                        </span>
                      </div>
                      <Badge className={cn(
                        'text-[10px] capitalize',
                        report.status === 'approved' ? 'bg-success/15 text-success' :
                        report.status === 'overdue' ? 'bg-destructive/15 text-destructive' :
                        report.status === 'submitted' ? 'bg-primary/15 text-primary' :
                        'bg-muted text-muted-foreground'
                      )}>
                        {report.status}
                      </Badge>
                    </div>
                  )
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quarterly Project Timeline - Inspired by PPM Dashboard Gantt */}
      <Card className="border border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-foreground">
            Quarterly Project Timeline
          </CardTitle>
          <CardDescription className="text-xs">
            Project duration overview across quarters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-1">
            {/* Timeline Header */}
            <div className="grid grid-cols-[180px_1fr] gap-3">
              <div />
              <div className="grid grid-cols-4 gap-1">
                {(quarterTimeline.labels.length > 0 ? quarterTimeline.labels : ['Q1', 'Q2', 'Q3', 'Q4']).map((q) => (
                  <div key={q} className="text-center text-[10px] text-muted-foreground font-medium py-1 bg-muted/50 rounded">
                    {q}
                  </div>
                ))}
              </div>
            </div>

            {/* Project Bars */}
            {timelineLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading project timeline...</div>
            ) : quarterTimeline.rows.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No projects found.</div>
            ) : (
              quarterTimeline.rows.map((project, i) => {
                const colors = [
                  'bg-destructive/70',
                  'bg-primary/70',
                  'bg-success/70',
                  'bg-secondary/80',
                ]
                return (
                  <div key={project.id} className="grid grid-cols-[180px_1fr] gap-3 items-center py-1.5">
                    <div className="flex flex-col gap-0">
                      <span className="text-xs font-medium text-foreground truncate">{project.code}</span>
                      <span className="text-[10px] text-muted-foreground truncate">{project.title}</span>
                    </div>
                    <div className="relative h-6 rounded bg-muted/30 overflow-hidden">
                      <div
                        className={cn('absolute top-0 h-full rounded flex items-center px-2', colors[i % colors.length])}
                        style={{ left: `${project.leftPct}%`, width: `${project.widthPct}%` }}
                      >
                        {project.widthPct >= 8 ? (
                          <span className="text-[9px] text-card font-medium truncate">
                            {project.progress}% complete
                          </span>
                        ) : null}
                      </div>
                      {project.widthPct < 8 ? (
                        <span
                          className={cn(
                            'absolute top-1/2 -translate-y-1/2 text-[9px] font-semibold',
                            colors[i % colors.length],
                          )}
                          style={{
                            left: project.endPct < 85 ? `calc(${project.endPct}% + 6px)` : 'auto',
                            right: project.endPct >= 85 ? '6px' : 'auto',
                          }}
                        >
                          {project.progress}%
                        </span>
                      ) : null}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
